/**
 * PartnersDropdown — navbar entry for the NoFee Partners portal.
 *
 * iter103 (Jun 2026) — Owner spec: removed "Schedule a Demo" from the
 * dropdown and promoted "Partner Login" into a highlighted gold CTA
 * button labelled "Already have an account? Sign In" pinned to the
 * bottom of the menu (mirrors the MyAccount dropdown convention).
 *
 *   💰  Partner Benefits       → /partners/partner-benefits
 *   🏪  Member Benefits        → /partners/member-benefits
 *   🏢  Become a Partner       → /partners/onboarding
 *   ─────────────────────────
 *   🟡  [BUTTON] Already have an account? Sign In   → /partners/login
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, ChevronDown, LogIn, BadgePercent, Store, UserPlus, ArrowRight } from 'lucide-react';

const buildItems = (t) => [
  {
    icon: BadgePercent,
    label: t('partners.nav.partner_benefits'),
    description: t('partners.nav.partner_benefits_desc'),
    href: '/partners/partner-benefits',
    testId: 'partners-dropdown-item-partner-benefits',
  },
  {
    icon: Store,
    label: t('partners.nav.member_benefits'),
    description: t('partners.nav.member_benefits_desc'),
    href: '/partners/member-benefits',
    testId: 'partners-dropdown-item-member-benefits',
  },
];

export default function PartnersDropdown() {
  const { t } = useTranslation();
  const ITEMS = buildItems(t);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isPartnersRoute =
    location.pathname === '/partners' || location.pathname.startsWith('/partners/');

  // Close on outside click + escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (item) => {
    setOpen(false);
    if (item.external) {
      window.location.href = item.href;
      return;
    }
    navigate(item.href);
  };

  return (
    <div ref={wrapRef} className="relative hidden lg:inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="navbar-partners-link"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all text-white/85 hover:text-gold-300"
      >
        {/* iter107 — Swapped office-building glyph for a people/group
            icon per owner spec; visually echoes the “association /
            community” framing of the Partners program.
            iter108 — Trigger is always white; only mouse hover turns
            it gold (same rule applied to every navbar item). */}
        <Users className="w-3.5 h-3.5" />
        {t('partners.nav.partners')}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="partners-dropdown-menu"
          className="absolute top-[calc(100%+10px)] right-0 w-[360px] rounded-2xl border border-white/[0.08] bg-gradient-to-b from-zinc-950 to-black shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
        >
          <div className="px-3 pt-3 pb-2">
            <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold">
              NoFee Partners
            </p>
            <p className="text-[12px] text-white/45 mt-0.5">
              {t('partners.nav.tagline')}
            </p>
          </div>
          <div className="h-px bg-white/[0.06] mx-2" />
          <div className="py-1">
            {ITEMS.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.label}
                  role="menuitem"
                  type="button"
                  onClick={() => handleSelect(it)}
                  data-testid={it.testId}
                  className="w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] focus:bg-white/[0.05] outline-none transition-colors group"
                >
                  <span className="w-9 h-9 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center flex-shrink-0 group-hover:border-gold-500/40 transition-colors">
                    <Icon className="w-4 h-4 text-gold-300" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-semibold text-white">{it.label}</span>
                    <span className="block text-[12px] text-white/55 leading-snug mt-0.5">
                      {it.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* iter108 — Pinned dual-CTA footer mirrors the MyAccount
              dropdown convention: a primary "Create Partner Account"
              gold pill stacked above a subtle "Already have an account?
              Sign In" link. The standalone "Become a Partner" menu row
              was removed since this CTA serves the same intent more
              prominently. */}
          <div className="px-3 pt-3 pb-3 border-t border-white/[0.06] mt-1 grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => handleSelect({ href: '/partners/onboarding' })}
              data-testid="partners-dropdown-item-create"
              className="group inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[13.5px] tracking-tight transition-all shadow-[0_12px_32px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
            >
              <UserPlus className="w-4 h-4" />
              Create Partner Account
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={() => handleSelect({ href: '/partners/login' })}
              data-testid="partners-dropdown-item-login"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-yellow-200 hover:text-yellow-100 font-bold text-[12.5px] transition-colors"
            >
              <LogIn className="w-3.5 h-3.5" />
              Already have an account? Sign In
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
